import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { DateTime } from 'luxon';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import {
  DataGridPro,
  GridApiPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon,
  Replay as ReplayIcon,
  EditCalendar as EditCalendarIcon,
} from '@mui/icons-material';

import { TripLeg, TripLegAddress } from '@/data/models/reservation.model';
import {
  TripLegStatus,
  tripStatusWithEtaList,
} from '@/data/models/trip-leg-status.model';
import { TransportationAttribute } from '@/data/models/transportation-attribute.model';
import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';

import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useLookups } from '@/hooks/useLookups';
import { useAppDispatch } from '@/hooks/useAppDispatch';

import * as lookupSlice from '@/data/lookup.slice';
import * as accountingSlice from '@/data/accounting.slice';
import * as driverSlice from '@/data/driver.slice';

import dateUtils from '@/services/DateUtils';

import AssignProviderDialog from '@/components/home/triplegs/AssignProviderDialog';
import ReopenTripLegDialog from '@/components/home/triplegs/ReopenTripLegDialog';
import EditTripLegTimesDialog from '@/components/home/triplegs/EditTripLegTimesDialog';
import triplegUtils from '@/services/triplegUtils';

const PREFIX = 'MonitorTripsTable';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
  chip: `${PREFIX}-chip`,
  loading: `${PREFIX}-loading`,
  wrapWhiteSpace: `${PREFIX}-wrapWhiteSpace`,
  tripDetail: `${PREFIX}-tripDetail`,
  actionContainer: `${PREFIX}-actionContainer`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    justifyContent: 'stretch',
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },

  [`& .${classes.chip}`]: {
    textTransform: 'uppercase',
  },

  [`& .${classes.grid} .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.${classes.wrapWhiteSpace}`]:
    {
      whiteSpace: 'normal',
    },

  [`& .${classes.tripDetail}`]: {
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    height: '100%',
  },

  [`& .${classes.actionContainer}`]: {
    justifyContent: 'flex-end !important',
  },
}));

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.loading}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

interface MonitorTripsTableProps {
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  rowCount: number;
  paginationChange: (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => void;
  sortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => void;
  tripLegs: TripLegSearchResponseItem[];
  loading: boolean;
  isSelectingTrips: boolean;
  showAssignAction?: boolean;
  showReopenAction?: boolean;
  showInProgress?: boolean;
  showEditTimesAction?: boolean;
  dataGridApiRef: MutableRefObject<GridApiPro>;
}

const MonitorTripsTable = ({
  page,
  pageSize,
  pageSizeOptions,
  rowCount,
  tripLegs,
  paginationChange,
  sortModelChange,
  loading,
  isSelectingTrips,
  showAssignAction = true,
  showReopenAction = true,
  showInProgress = false,
  showEditTimesAction = false,
  dataGridApiRef,
}: MonitorTripsTableProps) => {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const [dialogAssignOpen, setDialogAssignOpen] = React.useState(false);
  const [dialogReopenOpen, setDialogReopenOpen] = React.useState(false);
  const [dialogEditTimesOpen, setDialogEditTimesOpen] = React.useState(false);

  const [selectedTripLeg, setSelectedTripLeg] = React.useState<TripLeg>();

  useLookups(['passengerCareLevel', 'tripMode']);
  const timezones = useAppSelector(lookupSlice.selectTimeZones);
  const tripModes = useAppSelector(lookupSlice.selectTripModes);
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );

  const passengerCareLevels = useAppSelector(
    lookupSlice.selectPassengerCareLevels,
  );

  const driverLocationsByTripLegId = useAppSelector(
    driverSlice.selectDriverLocationsByTripLegId,
  );

  const inProgressDriverIds = useMemo(() => {
    const ids = new Set<number>();
    if (!tripLegs) return [] as number[];
    tripLegs.forEach((tl: any) => {
      const tripLegStatus = tl.tripLegStatusBC
        ? tl.tripLegStatusBC
        : tl.tripLeg?.tripLegStatusBC;
      const status = tripLegStatus?.toLowerCase();
      if (
        status &&
        tripStatusWithEtaList.includes(status as TripLegStatus) &&
        tl.driverID
      ) {
        ids.add(Number(tl.driverID));
      }
    });
    return Array.from(ids);
  }, [tripLegs]);

  useEffect(() => {
    inProgressDriverIds.forEach((driverId) => {
      dispatch(driverSlice.fetchDriverLocationAsync(driverId));
    });
  }, [dispatch, inProgressDriverIds]);

  const getAttributeByUUID = (uuid: string) => {
    return transportationAttributes.find(
      (a) => a.transportationAttributeUUID === uuid,
    );
  };

  const getAttributes = (
    tripLeg: TripLeg,
    attrBC: string,
  ): TransportationAttribute[] => {
    const attrs: TransportationAttribute[] = [];
    if (tripLeg.transportationAttributes) {
      tripLeg.transportationAttributes.forEach((ta) => {
        let attr = getAttributeByUUID(ta[0]);
        if (attr?.transportationAttributeCategoryBC === attrBC) {
          attrs.push(attr);
          for (let i = 1; i < ta.length; i++) {
            attr = getAttributeByUUID(ta[i]);
            if (attr) {
              attrs.push(attr);
            }
          }
        }
      });
    }
    return attrs;
  };

  const getPickupTime = useCallback(
    (params: GridCellParams) => {
      const tripLeg = params.row;
      if (tripLeg.isWillCall) {
        if (tripLeg.appointmentDate) {
          const m = DateTime.fromISO(tripLeg.appointmentDate)
            .setZone(dateUtils.WORK_TIME_ZONE)
            .plus({ hours: 11 });
          return (
            m.toLocaleString(DateTime.DATE_SHORT) +
            ', ' +
            t('reservation.willCallTime')
          );
        } else {
          return '';
        }
      } else {
        if (tripLeg.pickUpDueTimeUTC) {
          const tz = timezones.find(
            (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
          );
          const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
            zone: tz?.ianaStandard,
          });
          return m.toLocaleString(DateTime.DATETIME_SHORT);
        } else {
          return '';
        }
      }
    },
    [t, timezones],
  );

  // Same source data/logic as getPickupTime, but split into two lines (date, then time) for the
  // narrower two-line column layout instead of one long "date, time" string.
  const renderPickupTime = useCallback(
    (params: GridCellParams) => {
      const tripLeg = params.row;
      let dateText = '';
      let timeText = '';

      if (tripLeg.isWillCall) {
        if (tripLeg.appointmentDate) {
          const m = DateTime.fromISO(tripLeg.appointmentDate)
            .setZone(dateUtils.WORK_TIME_ZONE)
            .plus({ hours: 11 });
          dateText = m.toLocaleString(DateTime.DATE_SHORT);
          timeText = t('reservation.willCallTime');
        }
      } else if (tripLeg.pickUpDueTimeUTC) {
        const tz = timezones.find(
          (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
        );
        const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
          zone: tz?.ianaStandard,
        });
        dateText = m.toLocaleString(DateTime.DATE_SHORT);
        timeText = m.toLocaleString(DateTime.TIME_SIMPLE);
      }

      if (!dateText) {
        return '';
      }

      return (
        <Box sx={{ lineHeight: 1.3 }}>
          <Box>{dateText}</Box>
          <Box>{timeText}</Box>
        </Box>
      );
    },
    [t, timezones],
  );

  // "Pickup date/time" rendered as "Pickup" over "date/time" to fit the narrower column width.
  const renderPickupTimeHeader = useCallback(() => {
    const headerText = t('triplegInventory.pickupTime');
    const [firstWord, ...rest] = headerText.split(' ');
    return (
      <Box sx={{ lineHeight: 1.3 }}>
        <Box>{firstWord}</Box>
        <Box>{rest.join(' ')}</Box>
      </Box>
    );
  }, [t]);

  const getPassengerCareLevel = (params: GridCellParams): string => {
    const tripLeg = params.row;
    const passengerCareLevel = passengerCareLevels.find(
      (pcl) => pcl.passengerCareLevelBC === tripLeg.passengerCareLevelBC,
    );
    return passengerCareLevel?.passengerCareLevelName || '';
  };

  const renderAddress = (address: TripLegAddress) => {
    return (
      <Box>
        <Typography>{`${address.addressLine1}`}</Typography>
        <Typography>{`${address.city}, ${address.stateCode} ${address.zipCode}`}</Typography>
      </Box>
    );
  };

  const renderPickupAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const address = {
      addressLine1: tripLeg.pickUpAddressLine1,
      city: tripLeg.pickUpCity,
      stateCode: tripLeg.pickUpStateCode,
      zipCode: tripLeg.pickUpZipCode,
    };
    return renderAddress(address);
  };

  const renderDropoffAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const address = {
      addressLine1: tripLeg.dropOffAddressLine1,
      city: tripLeg.dropOffCity,
      stateCode: tripLeg.dropOffStateCode,
      zipCode: tripLeg.dropOffZipCode,
    };
    return renderAddress(address);
  };

  const renderETA = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const tripLegStatus = (
      tripLeg.tripLegStatusBC
        ? tripLeg.tripLegStatusBC
        : tripLeg.tripLeg?.tripLegStatusBC
    )?.toLowerCase();

    if (!tripStatusWithEtaList.includes(tripLegStatus as TripLegStatus)) {
      return <></>;
    }

    const location = driverLocationsByTripLegId[tripLeg.tripLegID];
    const seconds = location?.estimatedTimeOfArrivalSeconds;
    const eta = seconds
      ? `${Math.round(Number(seconds) / 60)} ${t('common.minutes')}`
      : '';
    return <>{eta}</>;
  };

  const renderTripId = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    if (!tripLeg) {
      return <></>;
    }
    const tripId = params.row.tripLegReferenceID;
    const passengerUUID = params.row.passengerUUID;
    const reservationID = params.row.reservationReferenceID;
    const link = `/passengers/${passengerUUID}/reservations/${reservationID}?tripLegID=${tripId}`;

    const tripIdText = (
      <Typography component="span" fontWeight={700} color="inherit">
        {tripId}
      </Typography>
    );

    return <Link to={link}>{tripIdText}</Link>;
  };

  const renderStatus = (params: GridCellParams): React.ReactElement => {
    const tripLegState = params.row.tripLegStatusBC;
    const chipColor = canReopenTripLeg(params.row) ? 'error' : 'primary';
    return tripLegState ? (
      <Chip color={chipColor} label={tripLegState} className={classes.chip} />
    ) : (
      <></>
    );
  };

  const renderTripMode = (params: GridCellParams): React.ReactElement => {
    var tripMode = tripModes.find(
      (m) => m.tripModeBC === params.row.tripModeBC,
    )?.tripModeName;
    return <Typography className={classes.tripDetail}>{tripMode}</Typography>;
  };

  const renderPassengerCareLevel = (
    params: GridCellParams,
  ): React.ReactElement => {
    const passengerCarelevel = getPassengerCareLevel(params);
    return (
      <Typography className={classes.tripDetail}>
        {passengerCarelevel}
      </Typography>
    );
  };

  const renderProviderName = (params: GridCellParams): React.ReactElement => {
    const providerName =
      (params.value as string) || params.row.providerName || '';
    return (
      <Typography className={classes.tripDetail}>{providerName}</Typography>
    );
  };

  const assignTripLeg = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setDialogAssignOpen(true);
  };

  const reopenTripLeg = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setDialogReopenOpen(true);
  };

  const canReopenTripLeg = (tripLeg: TripLeg) => {
    return triplegUtils.canReopenTripLeg(tripLeg);
  };

  const editTripTimes = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setDialogEditTimesOpen(true);
  };

  const closeAssignDialog = () => {
    setDialogAssignOpen(false);
  };

  const closeReopenDialog = () => {
    setDialogReopenOpen(false);
  };
  const closeEditTimesDialog = () => {
    setDialogEditTimesOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'tripLegReferenceID',
      headerName: t('triplegInventory.tripId'),
      width: 80,
      renderCell: renderTripId,
    },
    {
      field: 'pickUpDueTimeUTC',
      headerName: t('triplegInventory.pickupTime'),
      width: 90,
      sortable: true,
      valueGetter: getPickupTime,
      renderCell: renderPickupTime,
      renderHeader: renderPickupTimeHeader,
    },
    {
      field: 'pickUpAddress',
      headerName: t('triplegInventory.pickupAddress'),
      flex: 1.5,
      sortable: false,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropOffAddress',
      headerName: t('triplegInventory.dropoffAddress'),
      flex: 1.5,
      sortable: false,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'tripModeName',
      headerName: t('triplegInventory.tripMode'),
      flex: 1,
      renderCell: renderTripMode,
    },
    {
      field: 'passengerCareLevelName',
      headerName: t('triplegInventory.passengerCareLevel'),
      flex: 1,
      renderCell: renderPassengerCareLevel,
    },
    {
      field: 'providerName',
      headerName: t('triplegInventory.providerName'),
      flex: 1,
      renderCell: renderProviderName,
    },
    {
      field: 'tripLegStatusBC',
      headerName: t('triplegInventory.status'),
      width: 140,
      renderCell: renderStatus,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 100,
      sortable: false,
      filterable: false,
      cellClassName: classes.actionContainer,
      renderCell: (params: GridCellParams) => (
        <>
          {showReopenAction && canReopenTripLeg(params.row) && (
            <Tooltip title={t('triplegInventory.reopen')}>
              <span>
                <IconButton
                  data-testid="reopen"
                  onClick={(_evt) => reopenTripLeg(params.row)}
                  size="medium"
                  sx={{
                    visibility: canReopenTripLeg(params.row)
                      ? 'visible'
                      : 'hidden',
                  }}
                  color="error">
                  <ReplayIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showAssignAction && (
            <Tooltip title={t('triplegInventory.assign')}>
              <span>
                <IconButton
                  data-testid="edit"
                  disabled={params.row.tripLegStatusBC !== 'OPEN'}
                  onClick={(_evt) => assignTripLeg(params.row)}
                  size="medium"
                  color="primary">
                  <AssignmentTurnedInOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showEditTimesAction &&
            params.row.tripLegStatusBC?.toLocaleLowerCase() ===
              TripLegStatus.Accepted && (
              <Tooltip title={t('triplegInventory.editEventTimes')}>
                <span>
                  <IconButton
                    data-testid="edit"
                    onClick={(_evt) => editTripTimes(params.row)}
                    size="medium"
                    color="primary">
                    <EditCalendarIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
        </>
      ),
    },
  ];

  if (showInProgress) {
    const estimatedTimeColumn = {
      field: 'driverEstimatedTime',
      headerName: t('triplegInventory.eta'),
      width: 180,
      sortable: false,
      renderCell: renderETA,
      cellClassName: classes.wrapWhiteSpace,
    } as GridColDef;
    columns.splice(1, 0, estimatedTimeColumn);
  }

  return (
    <>
      <StyledPaper className={classes.container}>
        <DataGridPro
          getRowId={(row) => `${row.tripLegReferenceID}`}
          apiRef={dataGridApiRef}
          rows={tripLegs}
          rowCount={rowCount}
          columns={columns}
          pagination={true}
          paginationModel={{ page: page - 1, pageSize }}
          paginationMode="server"
          pageSizeOptions={pageSizeOptions}
          onPaginationModelChange={paginationChange}
          onSortModelChange={sortModelChange}
          autoHeight={true}
          loading={loading}
          className={classes.grid}
          checkboxSelection={isSelectingTrips}
          disableColumnSelector={true}
          hideFooter={false}
          sortingMode="server"
        />
        {selectedTripLeg && dialogAssignOpen && (
          <AssignProviderDialog
            tripLeg={selectedTripLeg}
            open={dialogAssignOpen}
            close={closeAssignDialog}
          />
        )}
        {selectedTripLeg && dialogReopenOpen && (
          <ReopenTripLegDialog
            tripLeg={selectedTripLeg}
            open={dialogReopenOpen}
            close={closeReopenDialog}
          />
        )}
        {selectedTripLeg && dialogEditTimesOpen && (
          <EditTripLegTimesDialog
            tripLeg={selectedTripLeg}
            open={dialogEditTimesOpen}
            close={closeEditTimesDialog}
          />
        )}
      </StyledPaper>
    </>
  );
};

export { MonitorTripsTable };
